import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import SalesRanking from '../components/Sales/SalesRanking';
import SalesSummaryTable from '../components/Sales/SalesSummaryTable';
import SalesTrend from '../components/Sales/SalesTrend';
import {
  fmtMoney, fmtNumber, fmtDate, fmtCyds, fmtRate, fmtPct,
} from '../components/Sales/format';
import { c, card } from '../theme';

// Open Market Commercial — Finance's Sales Status Report, rebuilt from the
// weekly Softpak export. The figure the business reads is Rev$/Cyd by cost
// center: whether pricing covers the cost of hauling.
//
// The period picker only chooses which export the SUMMARY shows. The trend
// always spans every committed export, because a trend that narrowed to the
// week you picked would be a single point.
export default function SalesReport() {
  const [batchId, setBatchId] = useState('');
  // One selected cost center, shared by the ranking, the table and all four
  // trend charts. Holding it here rather than in each component is what makes
  // "click a row, the charts follow" true of every way in.
  const [costCenter, setCostCenter] = useState(null);

  const periodsQ = useQuery({
    queryKey: ['sales-periods'],
    queryFn:  () => api.get('/sales-report/periods'),
  });
  const summaryQ = useQuery({
    queryKey: ['sales-summary', batchId || 'current'],
    queryFn:  () => api.get('/sales-report/summary', { params: batchId ? { batch_id: batchId } : {} }),
  });
  const trendQ = useQuery({
    queryKey: ['sales-trend'],
    queryFn:  () => api.get('/sales-report/trend'),
  });

  const periods = periodsQ.data || [];
  const summary = summaryQ.data;
  const trend   = trendQ.data || { periods: [], series: [] };

  // Rev$/Cyd in the export before the one on screen, per cost center, for the
  // table's change column. Read from the trend, which is scoped exactly as the
  // summary is, so a DVP compares their own yards with their own yards. Keyed
  // by cost center label, with null holding the company total.
  const priorPeriod = useMemo(() => {
    if (!summary?.period) return null;
    const idx = trend.periods.findIndex((p) => p.batch_id === summary.period.id);
    return idx > 0 ? trend.periods[idx - 1] : null;
  }, [trend.periods, summary?.period]);

  const previousByCostCenter = useMemo(() => {
    const map = new Map();
    if (!priorPeriod) return map;
    map.set(null, priorPeriod.rev_per_cyd);
    for (const sr of trend.series) {
      const point = sr.points.find((p) => p.batch_id === priorPeriod.batch_id);
      if (point) map.set(sr.cost_center, point.rev_per_cyd);
    }
    return map;
  }, [trend.series, priorPeriod]);

  const scopeText = summary?.scope === 'assigned'
    ? 'Showing the cost centers you have been granted.'
    : 'All cost centers.';

  const periodPicker = periods.length > 0 && (
    <label style={s.pickLabel}>
      <span style={s.pickCaption}>Export as of</span>
      <select
        id="sales-period"
        aria-label="Export period"
        value={batchId || summary?.period?.id || ''}
        onChange={(e) => {
          const chosen = periods.find((p) => p.id === e.target.value);
          // The current period is asked for as "current", not by id, so the
          // board keeps following the newest export when the next one lands.
          setBatchId(chosen && !chosen.is_current ? chosen.id : '');
        }}
        style={s.pick}
      >
        {periods.map((p) => (
          <option key={p.id} value={p.id}>{fmtDate(p.as_of_date)}{p.is_current ? ' (latest)' : ''}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Reports"
        title="Open Market Commercial"
        subtitle={`Sales status by cost center: revenue, monthly cubic yards and revenue per cubic yard, from the Softpak sales export. ${scopeText}`}
        meta={summary?.period ? (
          <span style={s.chip}>{summary.period.file_name}</span>
        ) : null}
        actions={periodPicker}
        alignActions="end"
      />

      {summaryQ.isLoading ? <LoadingSpinner /> : summaryQ.error ? (
        <div style={s.error}>{summaryQ.error.message}</div>
      ) : !summary?.period ? (
        <div style={s.emptyCard}>
          {summary?.scope === 'assigned'
            ? 'No sales data for the cost centers you have been granted yet.'
            : 'No sales export has been committed yet. Load one under Data Uploads → Sales Upload.'}
        </div>
      ) : (
        <>
          <Kpis summary={summary} trend={trend} />

          {summary.notes && <IngestNotes notes={summary.notes} />}

          <section style={s.card}>
            <SalesRanking
              rows={summary.rows}
              grandTotal={summary.grand_total}
              selected={costCenter}
              onSelect={setCostCenter}
            />
          </section>

          <section style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>Summary by Cost Center</div>
              <div style={s.cardSub}>As of {fmtDate(summary.period.as_of_date)}</div>
            </div>
            <SalesSummaryTable
              rows={summary.rows}
              grandTotal={summary.grand_total}
              previousByCostCenter={previousByCostCenter}
              previousLabel={priorPeriod ? fmtDate(priorPeriod.as_of_date) : null}
              selected={costCenter}
              onSelect={setCostCenter}
            />
          </section>
        </>
      )}

      <section style={s.card}>
        <div style={s.cardHead}>
          <div style={s.cardTitle}>Trend</div>
          <div style={s.cardSub}>
            {trend.periods.length === 1 ? 'One export so far' : `${fmtNumber(trend.periods.length)} exports`}
          </div>
        </div>
        {trendQ.isLoading ? <LoadingSpinner /> : trendQ.error
          ? <div style={s.error}>{trendQ.error.message}</div>
          : (
            <SalesTrend
              periods={trend.periods}
              series={trend.series}
              selected={costCenter}
              onClear={() => setCostCenter(null)}
            />
          )}
      </section>
    </div>
  );
}

function Kpis({ summary, trend }) {
  const g = summary.grand_total;
  // Change against the export before the one on screen. Read from the trend,
  // which is scoped the same way the summary is, so a DVP's delta compares
  // their own yards with their own yards.
  const idx  = trend.periods.findIndex((p) => p.batch_id === summary.period.id);
  const prev = idx > 0 ? trend.periods[idx - 1] : null;

  return (
    <div style={s.kpis}>
      <Kpi label="Total Rev$/Cyd" value={fmtRate(g.rev_per_cyd)}
           sub="All five materials over service volume" now={g.rev_per_cyd} before={prev?.rev_per_cyd} />
      <Kpi label="Total Revenue" value={fmtMoney(g.revenue)}
           sub={`${fmtMoney(g.ancillary_revenue)} of it Distance & Access`} now={g.revenue} before={prev?.revenue} />
      <Kpi label="Monthly Cyds" value={fmtCyds(g.volume)}
           sub="Shared containers counted once" now={g.volume} before={prev?.volume} />
      <Kpi label="3-Stream Participation" value={fmtPct(g.participation_rate)}
           sub={`${fmtNumber(g.participating)} of ${fmtNumber(g.accounts)} accounts`}
           now={g.participation_rate} before={prev?.participation_rate} />
      <Kpi label="Accounts" value={fmtNumber(g.accounts)}
           sub="Counted once in each cost center they touch" now={g.accounts} before={prev?.accounts} />
    </div>
  );
}

function Kpi({ label, value, sub, now, before }) {
  return (
    <div style={s.kpi}>
      <div style={s.kpiLabel}>{label}</div>
      <div style={s.kpiRow}>
        <span style={s.kpiValue}>{value}</span>
        <Movement now={now} before={before} />
      </div>
      <div style={s.kpiSub}>{sub}</div>
    </div>
  );
}

// Movement against the export before this one, stated plainly. No direction is
// called good: Rev$/Cyd has no agreed target yet, so an arrow coloured green or
// red would assert a verdict nobody has set.
function Movement({ now, before }) {
  if (before === null || before === undefined || !before) return null;
  const pct = ((now - before) / Math.abs(before)) * 100;
  if (Math.abs(pct) < 0.05) return <span style={s.kpiFlat}>no change</span>;
  return <span style={s.kpiDelta}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%</span>;
}

// What the upload found that no total shows. Only for unrestricted readers:
// these describe the whole export, including yards a scoped reader cannot see.
function IngestNotes({ notes }) {
  const items = [];
  for (const u of notes.untracked_materials || []) {
    items.push(
      `${fmtNumber(u.lines)} line(s) with Material "${u.material}" (${fmtMoney(u.revenue)}/month) are outside the five `
      + 'tracked materials and are in no total — as in the Finance workbook, pending classification.'
    );
  }
  if (notes.unresolved_lines) {
    items.push(`${fmtNumber(notes.unresolved_lines)} line(s) could not be placed in a cost center and are shown as Unresolved.`);
  }
  if (notes.excluded_lines) {
    items.push(`${fmtNumber(notes.excluded_lines)} line(s) were excluded under Finance's exclusion rules.`);
  }
  if (!items.length) return null;

  return (
    <div style={s.notes} role="note">
      <div style={s.notesHead}><AlertTriangle size={14} /> Not in these totals</div>
      <ul style={s.notesList}>{items.map((txt) => <li key={txt}>{txt}</li>)}</ul>
    </div>
  );
}

// Sized to sit beside Expense Analysis: 13px panel titles in near-black, 11px
// subtitles, 14px between panels. The board is wide, so it takes that page's
// width too rather than the narrower report width.
const s = {
  page:  { maxWidth: '1500px', margin: '0 auto', paddingBottom: '8px' },
  pickLabel:   { display: 'flex', flexDirection: 'column', gap: '3px' },
  pickCaption: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textFaint },
  pick:  { height: '30px', fontSize: '12px', fontFamily: 'inherit', padding: '0 8px', border: `1px solid ${c.border}`, borderRadius: '6px', background: c.surface, color: c.text, minWidth: '170px', cursor: 'pointer' },
  chip:  { fontSize: '11px', color: c.textMuted, background: c.canvas, borderRadius: '5px', padding: '2px 8px' },

  kpis:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))', gap: '14px', marginBottom: '14px' },
  kpi:       { ...card.sm, padding: '12px 14px' },
  kpiLabel:  { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  kpiRow:    { display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginTop: '3px' },
  kpiValue:  { fontSize: '20px', fontWeight: 700, color: c.text, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  kpiDelta:  { fontSize: '11px', fontWeight: 600, color: c.textMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  kpiFlat:   { fontSize: '11px', color: c.textFaint, whiteSpace: 'nowrap' },
  kpiSub:    { fontSize: '10.5px', color: c.textFaint, marginTop: '3px', lineHeight: 1.35 },

  card:      { ...card.sm, overflow: 'hidden', marginBottom: '14px' },
  cardHead:  { display: 'flex', alignItems: 'baseline', gap: '10px', padding: '14px 16px 10px', flexWrap: 'wrap' },
  cardTitle: { fontSize: '13px', fontWeight: 600, color: c.text },
  cardSub:   { fontSize: '11px', color: c.textFaint },
  notes:     { background: c.warningTint, border: `1px solid ${'#FDE68A'}`, borderRadius: '10px', padding: '11px 14px', marginBottom: '14px' },
  notesHead: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.warningText, marginBottom: '4px' },
  notesList: { margin: 0, paddingLeft: '18px', fontSize: '11.5px', color: c.warningText, lineHeight: 1.6 },
  emptyCard: { ...card.sm, padding: '56px 24px', textAlign: 'center', color: c.textMuted, fontSize: '13px', marginBottom: '14px' },
  error:     { padding: '16px', color: c.dangerText, fontSize: '12.5px' },
};
